// src/pages/EventoPage.tsx
import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { api } from "../services/api";
import Swal from "sweetalert2";
import EventoForm from "./EventoForm";

export interface Evento {
  id?: number;
  dataEvento?: string;
  dataInicial: string;
  dataFinal?: string | null;
  descricao: string;
  valorInicialCaixa: number;
  totalReceita: number;
  totalDespesa: number;
  observacao: string;
  ativo: boolean;
  descontoEquipePercentual: number;
}

const EventoPage: React.FC = () => {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [eventoEditando, setEventoEditando] = useState<Evento | null>(null);

  const carregarEventos = async () => {
    try {
      setLoading(true);
      const response = await api.get("/evento");
      setEventos(response.data || []);
      setError(null);
    } catch {
      setError("Não foi possível carregar os eventos.");
      Swal.fire("Erro!", "Não foi possível carregar os eventos.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarEventos();
  }, []);

  const handleEdit = (evento: Evento) => {
    setEventoEditando(evento);
    setShowForm(true);
  };

  const handleSave = async () => {
    setShowForm(false);
    setEventoEditando(null);
    await carregarEventos();
  };

  const formatDateTime = (date?: string | null) => {
    if (!date) return "Em aberto";

    const d = new Date(date);

    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };


  const eventoAtivo = eventos.find((e) => e.ativo);

  /* ------------------------------------
      CONFIRMAR ENCERRAMENTO
  ------------------------------------ */
  const confirmarEncerramento = (evento: Evento) => {
    Swal.fire({
      title: "Encerrar Evento?",
      text: "Deseja realmente encerrar o caixa? Esta ação atualizará a receita e finalizará o evento.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, encerrar!",
      cancelButtonText: "Cancelar",
    }).then((res) => {
      if (res.isConfirmed) encerrarEvento(evento.id!);
    });
  };

  /* ------------------------------------
      ENCERRAR EVENTO (API)
  ------------------------------------ */
  const encerrarEvento = async (eventoId: number) => {
    try {
      await api.post(`/evento/${eventoId}/encerrar`);

      Swal.fire({
        icon: "success",
        title: "Evento encerrado com sucesso!",
        timer: 2000,
        showConfirmButton: false,
      });

      carregarEventos();
    } catch (error: any) {
      Swal.fire(
        "Erro",
        error?.response?.data?.message || "Não foi possível encerrar o evento.",
        "error"
      );
    }
  };

  return (
    <div className="d-flex">
      <Sidebar />

      <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>📅 Eventos Cadastrados</h2>

          <button
            className="btn btn-primary"
            onClick={() => {
              setEventoEditando(null);
              setShowForm(!showForm);
            }}
          >
            {showForm ? "Fechar Formulário" : "+ Novo Evento"}
          </button>
        </div>

        {/* Abertura de vendas */}
        {eventoAtivo && (
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-success shadow-sm d-flex align-items-center gap-2"
              onClick={() => {
                window.location.href = `/vendas?eventoId=${eventoAtivo.id}`;
              }}
            >
              <i className="bi bi-cash-stack"></i>
              Abrir Vendas — {eventoAtivo.descricao}
            </button>
          </div>
        )}

        {!eventoAtivo && (
          <div
            className="alert alert-light border border-success-subtle text-success d-flex align-items-center gap-2 shadow-sm mb-3"
            style={{
              backgroundColor: "rgba(25, 135, 84, 0.05)",
              borderRadius: "8px",
            }}
          >
            <i className="bi bi-info-circle-fill"></i>
            Nenhum evento ativo no momento.
          </div>
        )}

        {/* Formulário */}
        {showForm && (
          <div className="mb-4">
            <EventoForm
              evento={eventoEditando ?? undefined}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEventoEditando(null);
              }}
            />
          </div>
        )}

        {loading && <p>Carregando eventos...</p>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && eventos.length > 0 && (
          <div className="table-responsive mt-4">
            <table className="table table-striped table-hover align-middle">
              <thead className="table-warning">
                <tr>
                  <th>ID</th>
                  <th>Descrição</th>
                  <th>Data Inicial</th>
                  <th>Data Final</th>
                  <th>Status</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>

              <tbody>
                {eventos.map((evento) => {
                  const encerrado = !evento.ativo;

                  return (
                    <tr
                      key={evento.id}
                      className={encerrado ? "table-secondary" : ""}
                    >
                      <td>{evento.id}</td>

                      <td>
                        {encerrado && (
                          <i className="bi bi-x-circle-fill text-danger me-2"></i>
                        )}
                        {evento.descricao}
                      </td>

                      <td>{formatDateTime(evento.dataInicial)}</td>
                      <td>{formatDateTime(evento.dataFinal)}</td>

                      <td>
                        {evento.ativo ? (
                          <span className="badge bg-success">Ativo</span>
                        ) : (
                          <span className="badge bg-secondary">Encerrado</span>
                        )}
                      </td>

                      <td className="text-end d-flex justify-content-end gap-2">

                        {/* Editar */}
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(evento)}
                        >
                          Editar
                        </button>

                        {/* Listar Vendas */}
                        <button
                          className="btn btn-sm btn-info text-white d-flex align-items-center gap-1"
                          onClick={() => {
                            window.location.href = `/evento/${evento.id}/vendas`;
                          }}
                        >
                          <i className="bi bi-list-ul"></i>
                          Listar Vendas
                        </button>

                        {/* Fiados */}
                        <button
                          className="btn btn-sm btn-warning d-flex align-items-center gap-1"
                          onClick={() => {
                            window.location.href = `/fiados?eventoId=${evento.id}`;
                          }}
                        >
                          <i className="bi bi-receipt"></i>
                          Fiados
                        </button>

                        {/* Resumo */}
                        <button
                          className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                          onClick={() => {
                            window.location.href = `/evento/${evento.id}/resumo`;
                          }}
                        >
                          <i className="bi bi-clipboard-data"></i>
                          Resumo
                        </button>

                        {/* Encerrar (somente quando ativo) */}
                        {evento.ativo && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => confirmarEncerramento(evento)}
                          >
                            Encerrar
                          </button>
                        )}


                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && eventos.length === 0 && (
          <div className="alert alert-warning mt-3">Nenhum evento cadastrado.</div>
        )}
      </div>
    </div>
  );
};

export default EventoPage;
